import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { ShieldCheckIcon, BarChart3Icon, FileTextIcon, ArrowRightIcon } from 'lucide-react';
export function HomePage() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-48">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6 md:px-12 sticky top-0 z-10" data-id="element-49">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-50">
          <div className="flex items-center space-x-2" data-id="element-51">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center" data-id="element-52">
              <ShieldCheckIcon className="text-white w-5 h-5" data-id="element-53" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight" data-id="element-54">
              IP Maturity<span className="text-blue-600" data-id="element-55">Check</span>
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => window.scrollTo(0, document.body.scrollHeight)} data-id="element-56">
            Sobre
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-grow" data-id="element-57">
        <section className="py-20 px-6 md:px-12 max-w-7xl mx-auto text-center md:text-left" data-id="element-58">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-59">
            <div className="space-y-8 animate-in slide-in-from-bottom-8 duration-700" data-id="element-60">
              <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight" data-id="element-61">
                Avalie a Maturidade de <br data-id="element-62" />
                <span className="text-blue-600" data-id="element-63">
                  Propriedade Intelectual
                </span>{' '}
                da sua empresa
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-lg leading-relaxed" data-id="element-64">
                Descubra em 5 minutos se sua empresa está protegendo e
                monetizando seus ativos intangíveis corretamente. Receba um
                relatório personalizado com recomendações.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-65">
                <Button size="lg" onClick={() => navigate('/avaliacao')} className="group" data-id="element-66">
                  Iniciar Avaliação Gratuita
                  <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-67" />
                </Button>
                <Button variant="outline" size="lg" data-id="element-68">
                  Saiba mais
                </Button>
              </div>
              <p className="text-sm text-slate-500 flex items-center justify-center md:justify-start gap-2" data-id="element-69">
                <ShieldCheckIcon className="w-4 h-4" data-id="element-70" />
                Metodologia baseada em frameworks internacionais
              </p>
            </div>

            <div className="relative hidden md:block animate-in fade-in duration-1000 delay-200" data-id="element-71">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-slate-100 rounded-full blur-3xl opacity-70" data-id="element-72"></div>
              <div className="relative bg-white p-8 rounded-2xl shadow-xl border border-slate-100 transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-73">
                <div className="flex items-center gap-4 mb-6 border-b border-slate-100 pb-4" data-id="element-74">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-75">
                    <BarChart3Icon className="text-blue-600 w-6 h-6" data-id="element-76" />
                  </div>
                  <div data-id="element-77">
                    <div className="font-bold text-slate-900" data-id="element-78">
                      Relatório de Diagnóstico
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-79">
                      Gerado automaticamente
                    </div>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-80">
                  <div className="h-4 bg-slate-100 rounded w-3/4" data-id="element-81"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/2" data-id="element-82"></div>
                  <div className="h-32 bg-slate-50 rounded border border-slate-100 flex items-center justify-center text-slate-400 text-sm" data-id="element-83">
                    Gráfico de Radar
                  </div>
                  <div className="flex gap-2 mt-4" data-id="element-84">
                    <div className="h-8 bg-blue-600 rounded w-1/3" data-id="element-85"></div>
                    <div className="h-8 bg-slate-200 rounded w-1/3" data-id="element-86"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="bg-white py-20 px-6 md:px-12 border-t border-slate-100" data-id="element-87">
          <div className="max-w-7xl mx-auto" data-id="element-88">
            <div className="text-center mb-16" data-id="element-89">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-90">
                Por que avaliar sua maturidade em PI?
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-91">
                A propriedade intelectual é frequentemente o ativo mais valioso
                de uma empresa moderna. Entender sua gestão é crucial para
                valorização e segurança.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-92">
              {[{
              icon: <ShieldCheckIcon className="w-8 h-8 text-blue-600" data-id="element-93" />,
              title: 'Identifique Riscos',
              desc: 'Descubra vulnerabilidades em seus processos de proteção e evite litígios custosos.'
            }, {
              icon: <BarChart3Icon className="w-8 h-8 text-blue-600" data-id="element-94" />,
              title: 'Compare Resultados',
              desc: 'Veja como sua empresa se posiciona em 5 dimensões críticas de gestão de PI.'
            }, {
              icon: <FileTextIcon className="w-8 h-8 text-blue-600" data-id="element-95" />,
              title: 'Plano de Ação',
              desc: 'Receba recomendações práticas e imediatas para elevar seu nível de maturidade.'
            }].map((feature, idx) => <div key={idx} className="p-8 rounded-xl bg-slate-50 border border-slate-100 hover:shadow-lg transition-shadow" data-id="element-96">
                  <div className="mb-6 bg-white w-16 h-16 rounded-xl flex items-center justify-center shadow-sm border border-slate-100" data-id="element-97">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-98">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-99">
                    {feature.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-12 px-6 text-center" data-id="element-100">
        <p data-id="element-101">© 2024 IP Maturity Check. Todos os direitos reservados.</p>
      </footer>
    </div>;
}